import type { ActiveHunt } from '../meta/save';
import { generateHuntOptions, labelFor, type HuntOption, DEN_LEADS } from '../meta/hunts';

export interface HuntBoardCallbacks {
  getActiveHunt: () => ActiveHunt | null;
  onAccept: (option: HuntOption) => void;
  onAbandon: () => void;
  onClose: () => void;
  /** K-2 LEADS section: has this world/checkpoints.ts checkpoint id been discovered yet? */
  isCheckpointDiscovered: (checkpointId: string) => boolean;
  /** K-2: has this lead's own den already been cleared (first-clear flag) — a cleared lead is omitted, not shown as a rumor about something already resolved. */
  isDenCleared: (leadId: 'rat_king_lair' | 'sewer_elite') => boolean;
}

/**
 * S11's bodega hunt board (PLAN-CITY §5: "bodega hunt board (pick target ->
 * tracked)"). A full-screen overlay, same idiom as `ui/pause.ts`'s two
 * screens (reuses its `.br3d-pausescreen-title`/`.br3d-menu-btn` classes
 * directly rather than inventing parallel ones) — driven by main.ts's single
 * `uiState` (this screen owns the `'huntboard'` value). Rebuilds its body
 * from the CURRENT active hunt every time it's shown (`refresh()`, called
 * from `setVisible(true)`), never caches a stale option list from a prior
 * visit — the same "read live state on open, don't assume the reference is
 * still current" discipline `PauseMenu.buildSettingsScreen` already applies
 * by reading `getSettings()` fresh.
 */
export class HuntBoard {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;

  constructor(private readonly callbacks: HuntBoardCallbacks) {
    this.root = document.createElement('div');
    this.root.className = 'br3d-pausescreen br3d-huntboard';
    this.root.style.display = 'none';

    const title = document.createElement('div');
    title.className = 'br3d-pausescreen-title';
    title.textContent = 'HUNT BOARD';

    this.body = document.createElement('div');
    this.body.className = 'br3d-huntboard-body';

    this.root.append(title, this.body, this.menuButton('BACK', false, () => this.callbacks.onClose()));
    document.body.append(this.root);
  }

  private menuButton(label: string, primary: boolean, onPress: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = primary ? 'br3d-menu-btn br3d-menu-btn-primary' : 'br3d-menu-btn';
    btn.textContent = label;
    btn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      onPress();
    });
    return btn;
  }

  private refresh(): void {
    this.body.replaceChildren();
    const active = this.callbacks.getActiveHunt();
    if (active) {
      const card = document.createElement('div');
      card.className = 'br3d-huntcard br3d-huntcard-active';
      const label = document.createElement('div');
      label.className = 'br3d-huntcard-title';
      label.textContent = labelFor(active.kind);
      const progress = document.createElement('div');
      progress.className = 'br3d-huntcard-progress';
      progress.textContent = `${active.killedCount} / ${active.targetCount}`;
      const reward = document.createElement('div');
      reward.className = 'br3d-huntcard-reward';
      reward.textContent = `Reward: ${active.rewardCoins} coins, ${active.rewardScrap} scrap`;
      card.append(label, progress, reward);
      this.body.append(card, this.menuButton('ABANDON HUNT', false, () => this.callbacks.onAbandon()));
    } else {
      const intro = document.createElement('div');
      intro.className = 'br3d-huntcard-progress';
      intro.textContent = 'Pick a target:';
      this.body.append(intro);
      for (const option of generateHuntOptions(3)) {
        const card = document.createElement('button');
        card.className = 'br3d-huntcard br3d-huntcard-option';
        const label = document.createElement('div');
        label.className = 'br3d-huntcard-title';
        label.textContent = `${labelFor(option.kind)} x${option.targetCount}`;
        const reward = document.createElement('div');
        reward.className = 'br3d-huntcard-reward';
        reward.textContent = `+${option.rewardCoins} coins, +${option.rewardScrap} scrap`;
        card.append(label, reward);
        card.addEventListener('pointerdown', (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.callbacks.onAccept(option);
        });
        this.body.append(card);
      }
    }
    this.appendLeadsSection();
  }

  /**
   * K-2: LEADS — den rumors, gated on the relevant checkpoint's own
   * discovery, cleared away once that den's first-clear flag is set. Always
   * appended below the hunt/pick-a-target content (whether or not a hunt is
   * active) — a lead isn't tied to the hunt-target loop, it's a standing
   * "here's somewhere to go" pointer. Renders nothing at all (no empty
   * section) if every gated lead is either not-yet-discovered or cleared.
   */
  private appendLeadsSection(): void {
    const visible = DEN_LEADS.filter((lead) => this.callbacks.isCheckpointDiscovered(lead.requiresCheckpoint) && !this.callbacks.isDenCleared(lead.id));
    if (visible.length === 0) return;
    const heading = document.createElement('div');
    heading.className = 'br3d-huntcard-title br3d-huntboard-leads-heading';
    heading.textContent = 'LEADS';
    this.body.append(heading);
    for (const lead of visible) {
      const card = document.createElement('div');
      card.className = 'br3d-huntcard br3d-huntcard-lead';
      card.textContent = lead.text;
      this.body.append(card);
    }
  }

  /** Called by main.ts's single `setUiState()`, alongside `pauseMenu.setVisible()` — see that function's own comment. */
  setVisible(visible: boolean): void {
    if (visible) this.refresh();
    this.root.style.display = visible ? 'flex' : 'none';
  }
}
